// src/pages/Orders.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from '../services/api';
import {
  Search, RefreshCw, ChevronLeft, ChevronRight, Package,
  X, CheckCircle, AlertTriangle, Edit2,
  Filter, SlidersHorizontal, Trash2, CheckSquare, Square, Mail, Plus
} from 'lucide-react';

const STATUS_COLORS = {
  'Bestellt':    'badge-yellow',
  'Erstattet':   'badge-green',
  'Eingereicht': 'badge-purple',
  'Privat':      'badge-purple',
  'Storno':      'badge-red',
  'Reviewed':    'badge-green',
};

const REVIEW_TYPES = ['Text', 'Bild', 'Video'];

const MESSENGER_ICONS = {
  'WeChat':    { emoji: '💬', color: '#07C160' },
  'Telegram':  { emoji: '✈️', color: '#229ED9' },
  'WhatsApp':  { emoji: '📱', color: '#25D366' },
  'Facebook':  { emoji: '👤', color: '#1877F2' },
  'Sonstiges': { emoji: '💭', color: '#888' },
};



const ALL_COLUMNS = [
  { key: 'date',       label: 'Datum' },
  { key: 'product',    label: 'Produkt' },
  { key: 'order_nr',   label: 'Bestellnr.' },
  { key: 'agent',      label: 'Agent' },
  { key: 'account',    label: 'Account' },
  { key: 'price',      label: 'Preis' },
  { key: 'tax_ref',    label: 'Prov.' },
  { key: 'commission', label: 'Commission' },
  { key: 'req_type',   label: 'Typ' },
  { key: 'status',     label: 'Status' },
  { key: 'betrag',     label: 'Betrag' },
  { key: 'review',     label: 'Review' },
];

const DEFAULT_FILTERS = { dateFrom: '', dateTo: '', agent: '', account: '', req_type: '', status: '', excludeStatus: [], excludeAccounts: [] };
const DEFAULT_COLS = ['date','product','order_nr','agent','account','price','tax_ref','commission','req_type','status','betrag','review'];

const emitOrdersUpdated = (source) => {
  window.dispatchEvent(new CustomEvent('orders-updated', {
    detail: { source, ts: Date.now() }
  }));
};

// ─── MESSENGER BADGE ───────────────────────────────────────────────────────────
function MessengerBadge({ platform }) {
  const m = MESSENGER_ICONS[platform] || MESSENGER_ICONS['WeChat'];
  return <span title={platform} style={{ fontSize: 13, cursor: 'default' }}>{m.emoji}</span>;
}

// ─── BULK AKTIONSLEISTE ────────────────────────────────────────────────────────
function BulkActionBar({ selectedIds, allOrders, onSelectAll, onClearAll, onDelete, onStatusChange }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const count = selectedIds.length;
  const allSelected = count > 0 && count === allOrders.length;

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    await onDelete(selectedIds);
    setConfirmDelete(false);
  };

  const handleStatusChange = async (s) => {
    if (!s) return;
    await onStatusChange(selectedIds, s);
    setBulkStatus('');
  };

  

  return (
    <div style={{
      background: 'var(--bg2)',
      border: `1px solid ${count > 0 ? 'var(--accent)' : 'var(--border)'}`,
      borderRadius: 10,
      padding: '10px 16px',
      marginBottom: 12,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      flexWrap: 'wrap',
      transition: 'border-color 0.2s',
    }}>
      {/* Alle auswählen Toggle */}
      <button
        onClick={allSelected ? onClearAll : onSelectAll}
        className="btn btn-ghost"
        style={{ padding: '5px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}
      >
        {allSelected
          ? <CheckSquare size={14} color="var(--accent)" />
          : <Square size={14} />
        }
        {allSelected ? 'Alle abwählen' : 'Alle auswählen'}
      </button>

      {/* Zähler */}
      <span style={{
        fontSize: 12,
        color: count > 0 ? 'var(--accent)' : 'var(--text3)',
        fontFamily: 'Syne',
        fontWeight: count > 0 ? 700 : 400,
        minWidth: 110,
      }}>
        {count > 0 ? `${count} ausgewählt` : 'Nichts ausgewählt'}
      </span>

      <div style={{ width: 1, height: 24, background: 'var(--border)' }} />

      {count > 0 && (
        <>
          {/* Status ändern */}
          <select
            value={bulkStatus}
            onChange={e => handleStatusChange(e.target.value)}
            style={{ fontSize: 12, padding: '5px 10px', minWidth: 160 }}
          >
            <option value="">Status ändern…</option>
            {['Bestellt','Erstattet','Eingereicht','Privat','Storno','Reviewed'].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          {/* Löschen */}
          <button
            className="btn"
            onClick={handleDelete}
            style={{
              fontSize: 12,
              padding: '5px 12px',
              background: confirmDelete ? 'var(--danger, #ef4444)' : 'transparent',
              color: confirmDelete ? 'white' : 'var(--danger, #ef4444)',
              border: '1px solid var(--danger, #ef4444)',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              transition: 'all 0.15s',
            }}
          >
            <Trash2 size={13} />
            {confirmDelete ? `Wirklich ${count} löschen?` : `Löschen (${count})`}
          </button>

          {confirmDelete && (
            <button
              className="btn btn-ghost"
              onClick={() => setConfirmDelete(false)}
              style={{ fontSize: 12, padding: '5px 10px' }}
            >
              Abbrechen
            </button>
          )}

          {/* Auswahl leeren */}
          <button
            onClick={onClearAll}
            className="btn btn-ghost"
            style={{ marginLeft: 'auto', padding: '5px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <X size={12} /> Auswahl leeren
          </button>
        </>
      )}
    </div>
  );
}

// ─── FILTER PANEL ──────────────────────────────────────────────────────────────
const ALL_STATUSES = ['Bestellt','Erstattet','Eingereicht','Privat','Storno','Reviewed'];
const FILTER_STATUS_COLORS = { Bestellt: '#f97316', Erstattet: '#3b82f6', Eingereicht: '#a855f7', Privat: '#6b7280', Storno: '#ef4444', Reviewed: '#22c55e' };

function FilterPanel({ filters, setFilters, options, onReset }) {
  const excludeStatus = filters.excludeStatus || [];
  const excludeAccounts = filters.excludeAccounts || [];

  const toggleExcludeStatus = (s) => setFilters(f => ({
    ...f,
    excludeStatus: (f.excludeStatus || []).includes(s) ? (f.excludeStatus || []).filter(x => x !== s) : [...(f.excludeStatus || []), s]
  }));
  const toggleExcludeAccount = (a) => setFilters(f => ({
    ...f,
    excludeAccounts: (f.excludeAccounts || []).includes(a) ? (f.excludeAccounts || []).filter(x => x !== a) : [...(f.excludeAccounts || []), a]
  }));

  return (
    <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16 }}>

      {/* Status - Chip Toggle (ausblenden) */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ color: 'var(--text3)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Status ausblenden:</span>
          {excludeStatus.length > 0 && (
            <button onClick={() => setFilters(f => ({ ...f, excludeStatus: [] }))}
              style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 10, cursor: 'pointer', padding: '1px 5px', borderRadius: 4 }}>✕ alle</button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {ALL_STATUSES.map(s => {
            const excluded = excludeStatus.includes(s);
            return (
              <button key={s} onClick={() => toggleExcludeStatus(s)} style={{
                border: `1px solid ${excluded ? FILTER_STATUS_COLORS[s] : 'var(--border)'}`,
                background: excluded ? FILTER_STATUS_COLORS[s] + '22' : 'transparent',
                color: excluded ? FILTER_STATUS_COLORS[s] : 'var(--text3)',
                borderRadius: 999, padding: '3px 12px', fontSize: 11, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'Syne',
                textDecoration: excluded ? 'line-through' : 'none',
                opacity: excluded ? 1 : 0.7,
                transition: 'all 0.15s'
              }}>
                {excluded && '✕ '}{s}
              </button>
            );
          })}
        </div>
      </div>

      {/* Accounts - Chip Toggle (ausblenden) */}
      {options.accounts?.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ color: 'var(--text3)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Accounts ausblenden:</span>
            {excludeAccounts.length > 0 && (
              <button onClick={() => setFilters(f => ({ ...f, excludeAccounts: [] }))}
                style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 10, cursor: 'pointer', padding: '1px 5px', borderRadius: 4 }}>✕ alle</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {options.accounts.map(a => {
              const excluded = excludeAccounts.includes(a);
              return (
                <button key={a} onClick={() => toggleExcludeAccount(a)} style={{
                  border: `1px solid ${excluded ? 'var(--accent2)' : 'var(--border)'}`,
                  background: excluded ? 'rgba(239,68,68,0.12)' : 'transparent',
                  color: excluded ? 'var(--accent2)' : 'var(--text3)',
                  borderRadius: 999, padding: '3px 12px', fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'Syne',
                  textDecoration: excluded ? 'line-through' : 'none',
                  opacity: excluded ? 1 : 0.7,
                  transition: 'all 0.15s'
                }}>
                  {excluded && '✕ '}{a.replace('Account ', 'Acc ')}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Datum + weitere Filter */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginTop: 4 }}>
        <div>
          <label style={{ display: 'block', marginBottom: 4, color: 'var(--text3)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase' }}>Datum von</label>
          <input type="date" value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, color: 'var(--text3)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase' }}>Datum bis</label>
          <input type="date" value={filters.dateTo} onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))} />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, color: 'var(--text3)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase' }}>Agent</label>
          <select value={filters.agent} onChange={e => setFilters(f => ({ ...f, agent: e.target.value }))}>
            <option value="">Alle Agenten</option>
            {options.agents.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, color: 'var(--text3)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase' }}>Review Typ</label>
          <select value={filters.req_type} onChange={e => setFilters(f => ({ ...f, req_type: e.target.value }))}>
            <option value="">Alle Typen</option>
            {options.reqTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onReset} style={{ width: '100%' }}>
            <X size={13} />Filter zurücksetzen
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── COLUMN TOGGLE ─────────────────────────────────────────────────────────────
function ColumnToggle({ visibleCols, setVisibleCols, onClose }) {
  return (
    <div style={{
      position: 'absolute', right: 0, top: '100%', zIndex: 100,
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 10, padding: 16, minWidth: 200,
      boxShadow: '0 8px 24px rgba(0,0,0,0.3)', marginTop: 4,
    }}>
      <div style={{ fontFamily: 'Syne', fontSize: 12, fontWeight: 700, marginBottom: 12, color: 'var(--text2)' }}>
        Spalten anzeigen
      </div>
      {ALL_COLUMNS.map(col => (
        <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={visibleCols.includes(col.key)}
            onChange={() => setVisibleCols(prev =>
              prev.includes(col.key) ? prev.filter(c => c !== col.key) : [...prev, col.key]
            )}
            style={{ width: 'auto' }}
          />
          <span style={{ color: 'var(--text)' }}>{col.label}</span>
        </label>
      ))}
      <button className="btn btn-ghost" onClick={onClose} style={{ width: '100%', marginTop: 8, fontSize: 12 }}>Schließen</button>
    </div>
  );
}

// ─── NEW ORDER MODAL ───────────────────────────────────────────────────────────
function NewOrderModal({ onClose, onDone }) {
  const [asin, setAsin] = useState('');
  const [accountList, setAccountList] = useState([]);
  const [productName, setProductName] = useState('');
  const [price, setPrice] = useState('');
  const [agentName, setAgentName] = useState('');
  const [platform, setPlatform] = useState('WeChat');
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [status, setStatus] = useState('Bestellt');
  const [taxRef, setTaxRef] = useState(true);
  const [reqType, setReqType] = useState('Text');
  const [commissions, setCommissions] = useState({ Text: 25, Bild: 30, Video: 35 });
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [orderNumbers, setOrderNumbers] = useState({});
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [defaultsLoaded, setDefaultsLoaded] = useState(false);

  useEffect(() => {
    API.get('/accounts').then(res => {
      setAccountList((res.data.accounts || []).map(a => a.account_name));
    }).catch(() => {});
  }, []);

  const toggleAccount = (acc) => {
    setSelectedAccounts(prev => {
      if (prev.includes(acc)) {
        const next = prev.filter(a => a !== acc);
        const nums = { ...orderNumbers };
        delete nums[acc];
        setOrderNumbers(nums);
        return next;
      }
      return [...prev, acc];
    });
  };

  const calcBetrag = (p, tax) => {
    const pr = parseFloat(p) || 0;
    return tax ? parseFloat((pr * 0.35 + 25).toFixed(2)) : 25;
  };

  const validate = () => {
    const errs = [];
    if (!productName.trim()) errs.push('Produktname ist Pflichtfeld');
    if (!price || isNaN(parseFloat(price))) errs.push('Preis ist Pflichtfeld');
    if (selectedAccounts.length === 0) errs.push('Mindestens ein Account auswählen');
    selectedAccounts.forEach(acc => {
      if (!(orderNumbers[acc] || '').trim().match(/^\d{3}-\d{7}-\d{7}$/))
        errs.push(`${acc}: Bestellnummer ungültig (Format: 302-1234567-1234567)`);
    });
    return errs;
  };

  const save = async () => {
    const errs = validate();
    if (errs.length > 0) { setErrors(errs); return; }
    setErrors([]);
    setSaving(true);
    const entries = selectedAccounts.map(acc => ({
      account_name: acc,
      req_type: reqType,
      order_number: (orderNumbers[acc] || '').trim(),
      asin: asin.trim() || null,
      product_name: productName.trim(),
      price: parseFloat(price),
      agent_name: agentName.trim() || null,
      platform,
      order_date: orderDate,
      status,
      tax_ref: taxRef,
      commission: 25,
      betrag: calcBetrag(price, taxRef),
    }));
    try {
      await API.post('/orders/bulk-create', { entries });
      emitOrdersUpdated('orders-new');
      onDone();
      onClose();
    } catch (err) {
      setErrors([err.response?.data?.error || err.message]);
    } finally {
      setSaving(false);
    }
  };

  const lbl = {
    display: 'block', marginBottom: 6, color: 'var(--text2)',
    fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 16, width: 740, maxHeight: '92vh', overflow: 'auto', padding: 28 }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ fontFamily: 'Syne', fontSize: 16, fontWeight: 800 }}>➕ Neue Bestellung(en)</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)' }}><X size={20} /></button>
        </div>

        {/* Produktdaten */}
        <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: 20, marginBottom: 20 }}>
          <div style={{ fontFamily: 'Syne', fontSize: 12, fontWeight: 700, marginBottom: 16, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Produktdaten
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>Produktname *</label>
              <input value={productName} onChange={e => setProductName(e.target.value)} placeholder="z.B. Beatbot Clarifying Kit..." />
            </div>
            <div>
              <label style={lbl}>ASIN</label>
              <input value={asin} onChange={e => setAsin(e.target.value.toUpperCase())} placeholder="B0GJ61BT5H" style={{ fontFamily: 'DM Mono' }} />
            </div>
            <div>
              <label style={lbl}>Preis (€) *</label>
              <input type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="19.99" step="0.01" min="0" />
            </div>
            <div>
              <label style={lbl}>Agent</label>
              <input value={agentName} onChange={e => setAgentName(e.target.value)} placeholder="Agent Name" />
            </div>
            <div>
              <label style={lbl}>Bestelldatum</label>
              <input type="date" value={orderDate} onChange={e => setOrderDate(e.target.value)} />
            </div>
            <div>
              <label style={lbl}>Status</label>
              <select value={status} onChange={e => setStatus(e.target.value)}>
                {['Bestellt','Erstattet','Eingereicht','Privat','Storno','Reviewed'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={lbl}>Messenger</label>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {Object.keys(MESSENGER_ICONS).map(m => (
                  <button key={m} onClick={() => setPlatform(m)} className="btn"
                    style={{ background: platform === m ? 'var(--accent)' : 'transparent', color: platform === m ? 'white' : 'var(--text2)', border: platform === m ? 'none' : '1px solid var(--border)', padding: '4px 8px', fontSize: 11 }}>
                    {MESSENGER_ICONS[m].emoji} {m}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label style={lbl}>Provision</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {[true, false].map(v => (
                  <button key={String(v)} onClick={() => setTaxRef(v)} className="btn"
                    style={{ background: taxRef === v ? 'var(--accent)' : 'transparent', color: taxRef === v ? 'white' : 'var(--text2)', border: taxRef === v ? 'none' : '1px solid var(--border)', padding: '6px 12px', fontSize: 12, flex: 1 }}>
                    {v ? 'Ja' : 'Nein'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Accounts + Bestellnummern */}
        <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: 20, marginBottom: 20 }}>
          <div style={{ fontFamily: 'Syne', fontSize: 12, fontWeight: 700, marginBottom: 4, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Accounts & Bestellnummern *
          </div>
          <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
            Accounts auswählen → Bestellnummer pro Account eingeben
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 10 }}>
            {accountList.map(acc => {
              const isSelected = selectedAccounts.includes(acc);
              return (
                <div key={acc} style={{
                  border: `1px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 8,
                  padding: '10px 12px',
                  background: isSelected ? 'rgba(99,102,241,0.07)' : 'var(--bg2)',
                  transition: 'all 0.15s',
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: isSelected ? 10 : 0 }}>
                    <input type="checkbox" checked={isSelected} onChange={() => toggleAccount(acc)}
                      style={{ width: 'auto', cursor: 'pointer' }} />
                    <span style={{ fontSize: 13, fontWeight: isSelected ? 600 : 400, color: isSelected ? 'var(--accent)' : 'var(--text2)' }}>
                      {acc}
                    </span>
                  </label>
                  {isSelected && (
                    <input
                      value={orderNumbers[acc] || ''}
                      onChange={e => setOrderNumbers(prev => ({ ...prev, [acc]: e.target.value }))}
                      placeholder="302-1234567-1234567"
                      style={{ fontFamily: 'DM Mono', fontSize: 12, padding: '6px 10px', width: '100%' }}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {selectedAccounts.length > 0 && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: 'rgba(99,102,241,0.1)', borderRadius: 8, fontSize: 12, color: 'var(--accent)' }}>
              <strong>{selectedAccounts.length}</strong> Account(s) ausgewählt → <strong>{selectedAccounts.length}</strong> Bestellung(en) werden angelegt
              {price && (
                <span style={{ marginLeft: 8, color: 'var(--text2)' }}>
                  · Betrag je: {calcBetrag(price, taxRef).toFixed(2).replace('.', ',')} €
                </span>
              )}
            </div>
          )}
        </div>

        {/* Fehler */}
        {errors.length > 0 && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
            {errors.map((e, i) => (
              <div key={i} style={{ fontSize: 12, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={12} /> {e}
              </div>
            ))}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            <CheckCircle size={14} />
            {saving ? 'Speichern...' : `${selectedAccounts.length || 0} Bestellung(en) anlegen`}
          </button>
        </div>

      </div>
    </div>
  );
}

// ─── ADD RELATED ORDER MODAL ──────────────────────────────────────────────────
function AddRelatedOrderModal({ sourceOrder, onClose, onDone }) {
  const [accountList, setAccountList] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    API.get('/accounts')
      .then(res => setAccountList((res.data.accounts || []).map(a => a.account_name)))
      .catch(err => setError(err.response?.data?.error || 'Accounts konnten nicht geladen werden.'));
  }, []);

  const save = async () => {
    const num = orderNumber.trim();
    if (!selectedAccount) {
      setError('Bitte einen Account auswählen.');
      return;
    }
    if (!/^\d{3}-\d{7}-\d{7}$/.test(num)) {
      setError('Bestellnummer ungültig (Format: 302-1234567-1234567).');
      return;
    }
    if (!sourceOrder) {
      setError('Ausgangsbestellung konnte nicht ermittelt werden.');
      return;
    }

    const isPrivate = sourceOrder.status === 'Privat';
    const price = Number.parseFloat(sourceOrder.price) || 0;
    const taxRef = isPrivate ? false : (sourceOrder.tax_ref ?? true);
    const commission = isPrivate ? 0 : (Number.parseFloat(sourceOrder.commission) || 25);
    const betrag = isPrivate ? 0 : Number((taxRef ? (price * 0.35 + commission) : commission).toFixed(2));

    const entry = {
      account_name: selectedAccount,
      order_number: num,
      asin: sourceOrder.asin || null,
      product_name: sourceOrder.product_name || null,
      price,
      agent_name: sourceOrder.agent_name || null,
      platform: sourceOrder.platform || 'WeChat',
      order_date: new Date().toISOString().split('T')[0],
      // Privat bleibt Privat; alle anderen historischen/abgeschlossenen Zustände
      // starten für die neue Bestellung wieder als "Bestellt".
      status: isPrivate ? 'Privat' : 'Bestellt',
      tax_ref: taxRef,
      commission,
      betrag,
      req_type: sourceOrder.req_type || null,
    };

    setSaving(true);
    setError('');
    try {
      const res = await API.post('/orders/bulk-create', { entries: [entry] });
      const data = res.data || {};
      if (data.inserted !== 1) {
        if (data.skipped > 0) {
          setError('Diese Bestellnummer ist bereits vorhanden.');
        } else {
          const reason = data.details?.errors?.[0]?.reason;
          setError(reason || 'Bestellung konnte nicht angelegt werden.');
        }
        return;
      }
      emitOrdersUpdated('orders-add-related');
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Bestellung konnte nicht angelegt werden.');
    } finally {
      setSaving(false);
    }
  };

  const resultingStatus = sourceOrder?.status === 'Privat' ? 'Privat' : 'Bestellt';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(5px)' }}>
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 14, width: 560, maxWidth: 'calc(100vw - 32px)', padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,.45)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 18 }}>
          <div>
            <h3 style={{ fontFamily: 'Syne', fontSize: 16, fontWeight: 800, margin: 0 }}>➕ Bestellung hinzufügen</h3>
            <p style={{ color: 'var(--text3)', fontSize: 12, marginTop: 5 }}>
              Produktdaten werden von der vorhandenen Bestellung übernommen.
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)' }}><X size={19} /></button>
        </div>

        <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 9, padding: 14, marginBottom: 18 }}>
          <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: 13, marginBottom: 5 }}>{sourceOrder?.product_name || 'Produkt'}</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', color: 'var(--text3)', fontSize: 11, fontFamily: 'DM Mono' }}>
            {sourceOrder?.asin && <span>ASIN: {sourceOrder.asin}</span>}
            {sourceOrder?.agent_name && <span>Agent: {sourceOrder.agent_name}</span>}
            <span>Status: {resultingStatus}</span>
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={{ display: 'block', marginBottom: 8, color: 'var(--text2)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase' }}>Account *</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: 8 }}>
            {accountList.map(acc => (
              <button
                key={acc}
                type="button"
                className="btn"
                onClick={() => setSelectedAccount(acc)}
                style={{
                  justifyContent: 'flex-start',
                  background: selectedAccount === acc ? 'var(--accent)' : 'var(--bg3)',
                  color: selectedAccount === acc ? 'white' : 'var(--text2)',
                  border: selectedAccount === acc ? '1px solid var(--accent)' : '1px solid var(--border)',
                  padding: '9px 12px',
                  fontSize: 12,
                }}
              >
                {selectedAccount === acc ? '✓ ' : ''}{acc}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={{ display: 'block', marginBottom: 7, color: 'var(--text2)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase' }}>Bestellnummer *</label>
          <input
            value={orderNumber}
            onChange={e => setOrderNumber(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !saving) save(); }}
            placeholder="302-1234567-1234567"
            autoFocus
            style={{ fontFamily: 'DM Mono' }}
          />
        </div>

        {error && (
          <div style={{ background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, padding: '9px 11px', color: '#f87171', fontSize: 12, marginBottom: 16 }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Abbrechen</button>
          <button className="btn btn-primary" onClick={save} disabled={saving || !selectedAccount || !orderNumber.trim()}>
            <Plus size={14} /> {saving ? 'Wird angelegt...' : 'Bestellung hinzufügen'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── EDIT MODAL ────────────────────────────────────────────────────────────────
function EditModal({ order, asin, productName, noAsin, onClose, onDone }) {
  const singleMode = !asin && !noAsin;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [reqType, setReqType] = useState('');
  const [commission, setCommission] = useState('');
  const [betrag, setBetrag] = useState('');
  const [status, setStatus] = useState('');
  const [taxRef, setTaxRef] = useState('');
  const [platform, setPlatform] = useState('');
  const [agentName, setAgentName] = useState('');
  const [autoCalc, setAutoCalc] = useState(true);
  
  // ══════════════════════════════════════════════════════════════════════════════
  // NEU: Preis-Edit State
  // ══════════════════════════════════════════════════════════════════════════════
  const [editPrice, setEditPrice] = useState('');
  const [priceOverride, setPriceOverride] = useState(false);
  
  const [activeTab, setActiveTab] = useState('orders');
  const [extraInfo, setExtraInfo] = useState('');
  const [description, setDescription] = useState('');
  const [productNameOverride, setProductNameOverride] = useState('');
  const [asinOverride, setAsinOverride] = useState('');
  const [asinGroupPreview, setAsinGroupPreview] = useState([]);
  const [asinGroupLoading, setAsinGroupLoading] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [showAddOrder, setShowAddOrder] = useState(false);
  const currentAsin = asin || order?.asin || null;

  useEffect(() => {
    if (singleMode && order) {
      setOrders([order]);
      setSelectedIds([order.id]);
      setEditPrice(order.price != null ? String(order.price) : '');
      setLoading(false);
    } else if (asin) {
      setLoading(true);
      API.get(`/orders/by-asin/${asin}`)
        .then(res => {
          const ords = res.data.orders || [];
          setOrders(ords);
          setSelectedIds(ords.map(o => o.id));
          if (ords[0]?.price != null) setEditPrice(String(ords[0].price));
        })
        .finally(() => setLoading(false));
    } else if (noAsin && productName) {
      setLoading(true);
      API.get('/orders', { params: { search: productName, limit: 500 } })
        .then(res => {
          const filtered = (res.data.orders || []).filter(o => o.product_name === productName);
          setOrders(filtered);
          setSelectedIds(filtered.map(o => o.id));
          if (filtered[0]?.price != null) setEditPrice(String(filtered[0].price));
        })
        .finally(() => setLoading(false));
    }
  }, [asin, noAsin, order, productName, singleMode]);

  // ASIN-Notizen laden
  useEffect(() => {
    if (!currentAsin) return;
    API.get(`/asin-notes/${currentAsin}`)
      .then(res => {
        setExtraInfo(res.data.extra_info || '');
        setDescription(res.data.description || '');
      })
      .catch(() => {});
  }, [currentAsin]);

  // ASIN Gruppen-Vorschau laden (±5 Tage, kein doppelter Account)
  useEffect(() => {
    if (!asinOverride.trim() || !order) { setAsinGroupPreview([]); return; }
    const asin = asinOverride.trim().toUpperCase();
    if (!/^B[A-Z0-9]{9}$/i.test(asin)) { setAsinGroupPreview([]); return; }
    setAsinGroupLoading(true);
    const orderDate = order.order_date ? new Date(order.order_date) : new Date();
    const from = new Date(orderDate); from.setDate(from.getDate() - 5);
    const to   = new Date(orderDate); to.setDate(to.getDate() + 5);
    API.get('/orders', {
      params: {
        search: order.product_name,
        limit: 200,
        status: 'Bestellt,Reviewed,Eingereicht,Privat'
      }
    }).then(res => {
      const all = res.data.orders || [];
      const seenAccounts = new Set();
      const filtered = all.filter(o => {
        if (o.asin) return false; // schon ASIN gesetzt
        if (!o.product_name) return false;
        if (o.product_name.trim().toLowerCase() !== (order.product_name || '').trim().toLowerCase()) return false;
        const t = o.order_date ? new Date(o.order_date).getTime() : 0;
        if (t < from.getTime() || t > to.getTime()) return false;
        if (seenAccounts.has(o.account_name)) return false;
        seenAccounts.add(o.account_name);
        return true;
      });
      setAsinGroupPreview(filtered);
    }).catch(() => setAsinGroupPreview([]))
    .finally(() => setAsinGroupLoading(false));
  }, [asinOverride, order, singleMode]);

  const saveNotes = async () => {
    if (!currentAsin) return;
    setSavingNotes(true);
    try {
      await API.put(`/asin-notes/${currentAsin}`, { extra_info: extraInfo, description });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
    } catch (err) {
      alert('Fehler beim Speichern: ' + err.message);
    } finally {
      setSavingNotes(false);
    }
  };

  const toggleAll = () => setSelectedIds(
    selectedIds.length === orders.length ? [] : orders.map(o => o.id)
  );

  const handleReqTypeChange = (type) => {
    setReqType(type);
    setCommission({ 'Text': '25', 'Bild': '30', 'Video': '35' }[type] || commission);
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // NEU: Preis-Änderungs-Handler mit Auto-Berechnung
  // ══════════════════════════════════════════════════════════════════════════════
  const handlePriceChange = (newPrice) => {
    const priceNum = parseFloat(newPrice) || 0;
    
    setEditPrice(newPrice);
    setPriceOverride(true);
    
    const comm = parseFloat(commission || order?.commission || 25) || 25;
    const tax = taxRef === 'Ja' || (taxRef === '' && order?.tax_ref);
    
    const newBetrag = tax 
      ? parseFloat((priceNum * 0.35 + comm).toFixed(2))
      : comm;
    
    setBetrag(String(newBetrag));
    setAutoCalc(false);
  };

  const calcBetrag = (comm, price, tax) => {
    const c = parseFloat(comm) || 0;
    const p = parseFloat(price) || 0;
    const t = tax === 'Ja' || tax === true;
    return t ? ((p * 0.35) + c).toFixed(2) : c.toFixed(2);
  };

  const save = async () => {
    if (selectedIds.length === 0) return;
    setSaving(true);
    const fields = {};
    
    console.log('💾 SAVE DEBUG', {
      priceOverride,
      editPrice,
      editPriceType: typeof editPrice,
      commission,
      commissionType: typeof commission,
      orderCommission: order?.commission
    });
    
    if (reqType) fields.req_type = reqType;
    if (commission) fields.commission = parseFloat(commission);
    if (status) fields.status = status;
    if (taxRef) fields.tax_ref = taxRef === 'Ja';
    if (platform) fields.platform = platform;
    if (agentName) fields.agent_name = agentName;
    if (betrag && !autoCalc) fields.betrag = parseFloat(betrag);
    if (productNameOverride.trim()) fields.product_name = productNameOverride.trim();
    if (asinOverride.trim()) fields.asin = asinOverride.trim().toUpperCase();
    
    // NEU: Preis überschreiben wenn geändert
    if (priceOverride && editPrice !== '') {
      const parsedPrice = parseFloat(editPrice);
      if (!isNaN(parsedPrice)) {
        fields.price = parsedPrice;
      }
    }

    try {
      await API.patch('/orders/bulk-update', { ids: selectedIds, fields });

      // Wenn ASIN gesetzt und Gruppe vorhanden → auch Gruppe updaten
      if (asinOverride.trim() && asinGroupPreview.length > 0) {
        const groupIds = asinGroupPreview.map(o => o.id);
        await API.patch('/orders/bulk-update', { ids: groupIds, fields: { asin: asinOverride.trim().toUpperCase() } });
      }
      emitOrdersUpdated('orders-edit');
      onDone();
      onClose();
    } catch (err) {
      alert('Fehler: ' + (err?.message || String(err)));
    } finally {
      setSaving(false);
    }
  };

  const formatEur = (v) => v ? parseFloat(v).toFixed(2).replace('.', ',') + ' €' : '-';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '-';
  const firstSelected = orders.find(o => selectedIds.includes(o.id));

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 16, width: 720, maxHeight: '90vh', overflow: 'auto', padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontFamily: 'Syne', fontSize: 16, fontWeight: 800, marginBottom: 4 }}>✏️ Bestellungen bearbeiten</h2>
            {(singleMode ? order?.asin : asin) ? (
              <a href={`https://www.amazon.de/dp/${singleMode ? order?.asin : asin}`} target="_blank" rel="noreferrer"
                style={{ color: 'var(--accent)', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                {singleMode ? (order?.product_name || 'Bestellung') : productName}
              </a>
            ) : (
              <span style={{ color: 'var(--text2)', fontSize: 13 }}>{order?.product_name || 'Bestellung'}</span>
            )}
            {(singleMode ? order?.asin : asin) && <p style={{ color: 'var(--text3)', fontSize: 11, fontFamily: 'DM Mono', marginTop: 2 }}>ASIN: {singleMode ? order?.asin : asin}</p>}
            {singleMode && <p style={{ color: 'var(--text3)', fontSize: 11, fontFamily: 'DM Mono', marginTop: 2 }}>Bestellnr.: {order?.order_number}</p>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)' }}><X size={20} /></button>
        </div>

        {loading ? (
          <p style={{ color: 'var(--text3)', textAlign: 'center', padding: 30 }}>Laden...</p>
        ) : (
          <>
            {/* Tab Navigation */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
              {[{ id: 'orders', label: '✏️ Bestellungen' }, { id: 'notes', label: '📝 Review-Infos' }].map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                  background: activeTab === tab.id ? 'var(--accent)' : 'transparent',
                  color: activeTab === tab.id ? '#fff' : 'var(--text3)',
                  border: 'none', borderRadius: '8px 8px 0 0', padding: '7px 16px',
                  cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'Syne', marginBottom: -1
                }}>{tab.label}</button>
              ))}
            </div>

            {/* Tab: Review-Infos */}
            {activeTab === 'notes' && (
              <div>
                <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
                  Diese Infos werden beim Review-Generieren automatisch verwendet — für alle Accounts dieser ASIN.
                </p>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, color: 'var(--text2)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase' }}>
                    🤖 KI-Hinweise <span style={{ color: 'var(--text3)', fontWeight: 400, textTransform: 'none' }}>— was soll der Review betonen?</span>
                  </label>
                  <textarea
                    value={extraInfo}
                    onChange={e => setExtraInfo(e.target.value)}
                    placeholder="z.B. 'Funktioniert ohne Strom, ideal für Camping. Passt in jeden Rucksack, keine Kapseln nötig.'"
                    style={{
                      width: '100%', boxSizing: 'border-box', minHeight: 100,
                      background: 'var(--bg3)', border: '1px solid var(--border)',
                      borderRadius: 8, padding: '10px 12px', color: 'var(--text1)',
                      fontSize: 13, fontFamily: 'inherit', resize: 'vertical', outline: 'none'
                    }}
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, color: 'var(--text2)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase' }}>
                    📋 Weitere Infos <span style={{ color: 'var(--text3)', fontWeight: 400, textTransform: 'none' }}>— interne Notizen, Besonderheiten</span>
                  </label>
                  <textarea
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="z.B. 'Achtung: Verkäufer möchte Review auf Englisch. Spezielle Verpackung erwähnen.'"
                    style={{
                      width: '100%', boxSizing: 'border-box', minHeight: 80,
                      background: 'var(--bg3)', border: '1px solid #f97316',
                      borderRadius: 8, padding: '10px 12px', color: 'var(--text1)',
                      fontSize: 13, fontFamily: 'inherit', resize: 'vertical', outline: 'none',
                      borderColor: description ? '#f9731655' : 'var(--border)'
                    }}
                  />
                  {description && (
                    <p style={{ fontSize: 11, color: '#f97316', marginTop: 4 }}>⚠️ Weitere Infos hinterlegt — in der Tabelle sichtbar</p>
                  )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={saveNotes} disabled={savingNotes} style={{
                    background: notesSaved ? '#2e7d32' : 'var(--accent)', color: '#fff',
                    border: 'none', borderRadius: 8, padding: '8px 20px',
                    cursor: savingNotes ? 'wait' : 'pointer', fontSize: 13, fontWeight: 600
                  }}>
                    {savingNotes ? 'Speichern…' : notesSaved ? '✅ Gespeichert!' : '💾 Speichern'}
                  </button>
                </div>
              </div>
            )}
            {activeTab === 'orders' && (
              <>
                {!singleMode && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                      <input type="checkbox" checked={selectedIds.length === orders.length} onChange={toggleAll} style={{ width: 'auto', cursor: 'pointer' }} />
                      <span style={{ fontSize: 12, color: 'var(--text2)', fontFamily: 'Syne' }}>Alle auswählen ({selectedIds.length}/{orders.length})</span>
                    </div>
                    <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                      <table>
                        <thead>
                          <tr>
                            <th style={{ width: 30 }}></th>
                            <th>Datum</th><th>Account</th><th>Preis</th><th>Prov.</th>
                            <th>Typ</th><th>Commission</th><th>Betrag</th><th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orders.map(order => (
                            <tr key={order.id} style={{ opacity: selectedIds.includes(order.id) ? 1 : 0.4 }}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedIds.includes(order.id)}
                                  onChange={() => setSelectedIds(prev => prev.includes(order.id) ? prev.filter(i => i !== order.id) : [...prev, order.id])}
                                  style={{ width: 'auto', cursor: 'pointer' }}
                                />
                              </td>
                              <td style={{ fontSize: 11, color: 'var(--text3)' }}>{formatDate(order.order_date)}</td>
                              <td style={{ fontSize: 12 }}>{order.account_name}</td>
                              <td style={{ fontSize: 12 }}>{formatEur(order.price)}</td>
                              <td><span className={`badge ${order.tax_ref ? 'badge-green' : 'badge-gray'}`}>{order.tax_ref ? 'Ja' : 'Nein'}</span></td>
                              <td style={{ fontSize: 12, color: 'var(--text2)' }}>{order.req_type || '-'}</td>
                              <td style={{ fontSize: 12 }}>{formatEur(order.commission)}</td>
                              <td style={{ fontSize: 12, color: 'var(--accent2)', fontWeight: 600 }}>{formatEur(order.betrag)}</td>
                              <td><span className={`badge ${STATUS_COLORS[order.status] || 'badge-gray'}`}>{order.status}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: 20, marginBottom: 20 }}>
                  <h3 style={{ fontFamily: 'Syne', fontSize: 13, fontWeight: 700, marginBottom: 16, color: 'var(--text2)' }}>
                    Auf {selectedIds.length} ausgewählte Bestellungen anwenden:
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    
                    {/* ══════════════════════════════════════════════════════════════════════════════ */}
                    {/* NEU: PREIS MANUELL BEARBEITEN */}
                    {/* ══════════════════════════════════════════════════════════════════════════════ */}
                    {true && (
                      <div style={{ gridColumn: '1 / -1', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 8, padding: '12px 16px' }}>
                        <label style={{ display: 'block', marginBottom: 8, color: '#f97316', fontSize: 11, fontFamily: 'Syne', fontWeight: 700, textTransform: 'uppercase' }}>
                          💰 Preis manuell korrigieren
                          <span style={{ color: 'var(--text3)', fontWeight: 400, marginLeft: 8, textTransform: 'none' }}>
                            — bei falscher Preis-Erkennung aus Email
                          </span>
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editPrice}
                            onChange={e => handlePriceChange(e.target.value)}
                            placeholder="19.99"
                            style={{ 
                              width: 120, 
                              fontFamily: 'DM Mono', 
                              fontSize: 14, 
                              fontWeight: 600,
                              color: priceOverride ? '#f97316' : 'var(--text2)',
                              padding: '8px 12px'
                            }}
                          />
                          <span style={{ fontSize: 14, color: 'var(--text3)' }}>€</span>
                          <span style={{ fontSize: 13, color: 'var(--text3)' }}>→</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 2 }}>
                              Neuer Betrag: <strong style={{ color: '#f97316', fontSize: 14 }}>
                                {editPrice && commission ? calcBetrag(commission || (order?.commission || 25), editPrice, taxRef || (order?.tax_ref ? 'Ja' : 'Nein')).replace('.', ',') + ' €' : '-'}
                              </strong>
                            </div>
                            <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'DM Mono' }}>
                              Formel: ({editPrice || 0} × 0,35) + {commission || order?.commission || 25}
                            </div>
                          </div>
                        </div>
                        {priceOverride && (
                          <div style={{ marginTop: 8, fontSize: 11, color: '#f97316', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <AlertTriangle size={12} />
                            Preis wurde manuell geändert und wird beim Speichern überschrieben
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <label style={{ display: 'block', marginBottom: 6, color: 'var(--text2)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase' }}>Review Typ</label>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {REVIEW_TYPES.map(t => (
                          <button
                            key={t}
                            onClick={() => handleReqTypeChange(t)}
                            className="btn"
                            style={{ background: reqType === t ? 'var(--accent)' : 'transparent', color: reqType === t ? 'white' : 'var(--text2)', border: reqType === t ? 'none' : '1px solid var(--border)', padding: '6px 12px', fontSize: 12, flex: 1 }}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 6, color: 'var(--text2)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase' }}>Commission (€)</label>
                      <input type="number" value={commission} onChange={e => setCommission(e.target.value)} placeholder="z.B. 25" step="0.01" />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 6, color: 'var(--text2)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase' }}>Zusätzliche Provision</label>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {['Ja', 'Nein'].map(v => (
                          <button
                            key={v}
                            onClick={() => setTaxRef(v)}
                            className="btn"
                            style={{ background: taxRef === v ? 'var(--accent)' : 'transparent', color: taxRef === v ? 'white' : 'var(--text2)', border: taxRef === v ? 'none' : '1px solid var(--border)', padding: '6px 12px', fontSize: 12, flex: 1 }}
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 6, color: 'var(--text2)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase' }}>Messenger</label>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {Object.keys(MESSENGER_ICONS).map(m => (
                          <button
                            key={m}
                            onClick={() => setPlatform(m)}
                            className="btn"
                            style={{ background: platform === m ? 'var(--accent)' : 'transparent', color: platform === m ? 'white' : 'var(--text2)', border: platform === m ? 'none' : '1px solid var(--border)', padding: '4px 8px', fontSize: 11 }}
                          >
                            {MESSENGER_ICONS[m].emoji} {m}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 6, color: 'var(--text2)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase' }}>Status</label>
                      <select value={status} onChange={e => setStatus(e.target.value)}>
                        <option value="">— nicht ändern —</option>
                        {['Bestellt','Erstattet','Eingereicht','Privat','Storno','Reviewed'].map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 6, color: 'var(--text2)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase' }}>Agent Name</label>
                      <input value={agentName} onChange={e => setAgentName(e.target.value)} placeholder="Agent Name (leer = nicht ändern)" />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 6, color: 'var(--text2)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase' }}>Produktname überschreiben</label>
                      <input value={productNameOverride} onChange={e => setProductNameOverride(e.target.value)} placeholder="Neuer Produktname (leer = nicht ändern)" />
                    </div>

                    {(singleMode || noAsin) && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ display: 'block', marginBottom: 6, color: 'var(--accent)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase' }}>
                          {order?.asin ? `ASIN ändern (aktuell: ${order.asin})` : 'ASIN hinzufügen'}
                        </label>
                        <input
                          value={asinOverride}
                          onChange={e => setAsinOverride(e.target.value.toUpperCase())}
                          placeholder="B0XXXXXXXXXX"
                          style={{ fontFamily: 'DM Mono', maxWidth: 220 }}
                        />
                        {asinOverride && !/^B[A-Z0-9]{9}$/i.test(asinOverride) && (
                          <p style={{ fontSize: 11, color: '#f87171', marginTop: 4 }}>⚠️ Ungültige ASIN (muss mit B beginnen, 10 Zeichen)</p>
                        )}
                        {asinGroupLoading && <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>⏳ Suche Gruppe...</p>}
                        {!asinGroupLoading && asinGroupPreview.length > 0 && (
                          <div style={{ marginTop: 8, background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 8, padding: '8px 12px' }}>
                            <p style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600, marginBottom: 6 }}>
                              📦 {asinGroupPreview.length} weitere Bestellung(en) gefunden (gleicher Name ±5 Tage, je 1x pro Account) — ASIN wird auch dort gesetzt:
                            </p>
                            {asinGroupPreview.map(o => (
                              <div key={o.id} style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'DM Mono' }}>
                                {o.account_name} · {o.order_number} · {new Date(o.order_date).toLocaleDateString('de-DE')}
                              </div>
                            ))}
                          </div>
                        )}
                        {!asinGroupLoading && asinOverride && /^B[A-Z0-9]{9}$/i.test(asinOverride) && asinGroupPreview.length === 0 && (
                          <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Keine weiteren passenden Bestellungen ohne ASIN gefunden.</p>
                        )}
                      </div>
                    )}

                    <div style={{ gridColumn: '1 / -1' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <label style={{ color: 'var(--text2)', fontSize: 11, fontFamily: 'Syne', textTransform: 'uppercase' }}>Betrag (€)</label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text3)', cursor: 'pointer' }}>
                          <input type="checkbox" checked={autoCalc} onChange={e => setAutoCalc(e.target.checked)} style={{ width: 'auto' }} />
                          Auto (Preis × 0,35 + Commission)
                        </label>
                      </div>
                      {!autoCalc ? (
                        <input type="number" value={betrag} onChange={e => setBetrag(e.target.value)} placeholder="Manueller Betrag" step="0.01" style={{ maxWidth: 200 }} />
                      ) : commission && firstSelected && (
                        <p style={{ fontSize: 12, color: 'var(--accent2)', marginTop: 4 }}>
                          Beispiel: {calcBetrag(commission, editPrice || firstSelected.price, taxRef || (firstSelected.tax_ref ? 'Ja' : 'Nein'))} €
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' }}>
                  <button
                    className="btn btn-ghost"
                    onClick={() => setShowAddOrder(true)}
                    style={{ marginRight: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <Plus size={14} /> Bestellung hinzufügen
                  </button>
                  <button className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
                  <button className="btn btn-primary" onClick={save} disabled={saving || selectedIds.length === 0}>
                    <CheckCircle size={14} />
                    {saving ? 'Speichern...' : `${selectedIds.length} Bestellung(en) speichern`}
                    </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
      {showAddOrder && (
        <AddRelatedOrderModal
          sourceOrder={order || firstSelected}
          onClose={() => setShowAddOrder(false)}
          onDone={() => {
            setShowAddOrder(false);
            onDone();
            onClose();
          }}
        />
      )}
    </div>
  );
}

// ─── REFUND WORKFLOW ──────────────────────────────────────────────────────────
// Erstattungen werden ausschließlich auf der Seite "Erstattungen" verwaltet.


// ─── HAUPTSEITE ────────────────────────────────────────────────────────────────
export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [accountList, setAccountList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [options, setOptions] = useState({ agents: [], accounts: [], reqTypes: [] });
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [visibleCols, setVisibleCols] = useState(DEFAULT_COLS);
  const [colToggleOpen, setColToggleOpen] = useState(false);
  const [agentSort, setAgentSort] = useState(false);

  // ── NEU: Mehrfachauswahl ──
  const [selectedIds, setSelectedIds] = useState([]);

  // ── Mail Import ──
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleMailImport = async () => {
    setImporting(true);
    setImportResult(null);
    try {
      const res = await API.post('/mail/import');
      setImportResult(res.data);
      if (res.data.inserted > 0) { fetchOrders(); fetchStats(); }
    } catch (err) {
      setImportResult({ error: err.response?.data?.error || err.message });
    } finally {
      setImporting(false);
    }
  };

  const activeFiltersCount = Object.entries(filters).filter(([k, v]) =>
    Array.isArray(v) ? v.length > 0 : v !== ''
  ).length;

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (search) params.search = search;
      if (filters.status) params.status = filters.status;
      if (filters.agent) params.agent = filters.agent;
      if (filters.account) params.account = filters.account;
      if (filters.req_type) params.req_type = filters.req_type;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.excludeStatus?.length > 0) params.excludeStatus = filters.excludeStatus.join(',');
      if (filters.excludeAccounts?.length > 0) params.excludeAccounts = filters.excludeAccounts.join(',');

      const res = await API.get('/orders', { params });
      setOrders(res.data.orders);
      setTotal(res.data.total);
      setPages(res.data.pages);
      setSelectedIds([]); // Auswahl bei neuem Laden leeren
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await API.get('/orders/stats');
      setStats(res.data);
    } catch {}
  }, []);

  const fetchOptions = useCallback(async () => {
    try {
      const res = await API.get('/orders/filter-options');
      setOptions(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { fetchStats(); fetchOptions(); }, [fetchStats, fetchOptions]);
  useEffect(() => {
    API.get('/accounts').then(res => {
      setAccountList((res.data.accounts || []).map(a => a.account_name));
    });
  }, []);

  useEffect(() => {
    const onOrdersUpdated = () => { fetchOrders(); fetchStats(); };
    window.addEventListener('orders-updated', onOrdersUpdated);
    return () => window.removeEventListener('orders-updated', onOrdersUpdated);
  }, [fetchOrders, fetchStats]);

  // ── Bulk: Löschen ──
  const handleBulkDelete = async (ids) => {
    try {
      await API.post('/orders/bulk-delete', { ids });
      emitOrdersUpdated('orders-delete');
      fetchOrders();
      fetchStats();
    } catch (err) {
      alert('Fehler beim Löschen: ' + (err.response?.data?.error || err.message));
    }
  };

  // ── Bulk: Status ändern ──
  const handleBulkStatus = async (ids, status) => {
    try {
      await API.patch('/orders/bulk-update', { ids, fields: { status } });
      emitOrdersUpdated('orders-status');
      fetchOrders();
      fetchStats();
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.error || err.message));
    }
  };

  const toggleRow = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  // Agent-Sort
  const displayOrders = agentSort
    ? [...orders].sort((a, b) => {
        const agA = a.agent_name || '繾繾繾';
        const agB = b.agent_name || '繾繾繾';
        if (agA !== agB) return agA.localeCompare(agB);
        return (a.account_name || '').localeCompare(b.account_name || '');
      })
    : orders;

  const agentGroups = agentSort
    ? displayOrders.reduce((acc, order, idx) => {
        const agent = order.agent_name || '(kein Agent)';
        if (idx === 0 || (displayOrders[idx - 1].agent_name || '(kein Agent)') !== agent) {
          acc[idx] = { name: agent, count: displayOrders.filter(o => (o.agent_name || '(kein Agent)') === agent).length };
        }
        return acc;
      }, {})
    : {};

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE') : '-';
  const formatEur = (v) => v ? parseFloat(v).toFixed(2).replace('.', ',') + ' €' : '-';
  const col = (key) => visibleCols.includes(key);

  const openEdit = (order) => {
    if (order.asin) setEditModal({ asin: order.asin, productName: order.product_name, order });
    else if (order.product_name) setEditModal({ productName: order.product_name, noAsin: true, order });
    else setEditModal({ order });
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Bestellungen</h1>
          <p style={{ color: 'var(--text2)', marginTop: 4, fontSize: 13 }}>{total} Bestellungen</p>
        </div>

        {/* ── MAIL IMPORT ── */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
            <button
              className="btn btn-ghost"
              onClick={handleMailImport}
              disabled={importing}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Mail size={14} />
              {importing ? 'Importiere...' : 'Mails importieren'}
            </button>
            {importResult && !importing && (
              <span style={{ fontSize: 11, color: importResult.error ? 'var(--danger, #ef4444)' : 'var(--accent2)', fontFamily: 'Syne' }}>
                {importResult.error ? `❌ ${importResult.error}` : `✅ ${importResult.inserted} neu · ${importResult.skipped} Skip`}
              </span>
            )}
          </div>

          <button className="btn btn-primary" onClick={() => setShowNewOrder(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> Neue Bestellung
          </button>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="btn btn-ghost"
            onClick={() => setAgentSort(s => !s)}
            style={{
              background: agentSort ? 'rgba(99,102,241,0.15)' : 'transparent',
              border: agentSort ? '1px solid var(--accent)' : '1px solid var(--border)',
              color: agentSort ? 'var(--accent)' : 'var(--text2)',
              display: 'flex', alignItems: 'center', gap: 6
            }}
          >
            👤 Nach Agent
            {agentSort && <span style={{ background: 'var(--accent)', color: 'white', borderRadius: 10, fontSize: 10, padding: '1px 6px' }}>AN</span>}
          </button>

          <button className="btn btn-ghost" onClick={() => setFilterOpen(!filterOpen)} style={{ position: 'relative' }}>
            <Filter size={14} />Filter
            {activeFiltersCount > 0 && (
              <span style={{ background: 'var(--accent)', color: 'white', borderRadius: 10, fontSize: 10, padding: '1px 5px', marginLeft: 2 }}>
                {activeFiltersCount}
              </span>
            )}
          </button>

          <div style={{ position: 'relative' }}>
            <button className="btn btn-ghost" onClick={() => setColToggleOpen(!colToggleOpen)}>
              <SlidersHorizontal size={14} />Spalten
            </button>
            {colToggleOpen && (
              <ColumnToggle visibleCols={visibleCols} setVisibleCols={setVisibleCols} onClose={() => setColToggleOpen(false)} />
            )}
          </div>

          <button className="btn btn-ghost" onClick={fetchOrders}><RefreshCw size={14} /></button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid-4" style={{ marginBottom: 16 }}>
          {[
            { label: 'Heute', value: stats.today?.count || 0, sub: formatEur(stats.today?.total) },
            { label: 'Gesamt', value: stats.total?.count || 0, sub: formatEur(stats.total?.total) },
            { label: 'Bestellt', value: stats.byStatus?.find(s => s.status === 'Bestellt')?.count || 0, color: 'var(--accent3)' },
            { label: 'Erstattet', value: stats.byStatus?.find(s => s.status === 'Erstattet')?.count || 0, color: 'var(--accent2)' },
          ].map((s, i) => (
            <div key={i} className="card">
              <div style={{ color: 'var(--text3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Syne', marginBottom: 8 }}>{s.label}</div>
              <div style={{ fontSize: 28, fontFamily: 'Syne', fontWeight: 800, color: s.color || 'var(--text)' }}>{s.value}</div>
              {s.sub && <div style={{ color: 'var(--text3)', fontSize: 12, marginTop: 4 }}>{s.sub}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Suche */}
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
        <input
          placeholder="Suche nach Produkt, Bestellnummer, ASIN, Agent..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{ paddingLeft: 36 }}
        />
      </div>

      {/* Filter Panel */}
      {filterOpen && (
        <FilterPanel
          filters={filters}
          setFilters={(fn) => { setFilters(fn); setPage(1); }}
          options={options}
          onReset={() => { setFilters(DEFAULT_FILTERS); setPage(1); }}
        />
      )}

      {/* ── BULK AKTIONSLEISTE ── */}
      <BulkActionBar
        selectedIds={selectedIds}
        allOrders={orders}
        onSelectAll={() => setSelectedIds(orders.map(o => o.id))}
        onClearAll={() => setSelectedIds([])}
        onDelete={handleBulkDelete}
        onStatusChange={handleBulkStatus}
      />

      {/* Tabelle */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Laden...</div>
        ) : orders.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <Package size={32} style={{ color: 'var(--text3)', marginBottom: 12 }} />
            <p style={{ color: 'var(--text3)' }}>Keine Bestellungen gefunden</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                {/* Checkbox-Kopf: alle auf dieser Seite auswählen */}
                <th style={{ width: 40, paddingLeft: 14 }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.length === orders.length && orders.length > 0}
                    onChange={() =>
                      selectedIds.length === orders.length
                        ? setSelectedIds([])
                        : setSelectedIds(orders.map(o => o.id))
                    }
                    style={{ width: 'auto', cursor: 'pointer' }}
                    title="Alle auf dieser Seite"
                  />
                </th>
                {col('date') && <th>Datum</th>}
                {col('product') && <th>Produkt</th>}
                {col('order_nr') && <th>Bestellnr.</th>}
                {col('agent') && <th>Agent</th>}
                {col('account') && <th>Account</th>}
                {col('price') && <th>Preis</th>}
                {col('tax_ref') && <th>Prov.</th>}
                {col('commission') && <th>Commission</th>}
                {col('req_type') && <th>Typ</th>}
                {col('status') && <th>Status</th>}
                {col('betrag') && <th>Betrag</th>}
                {col('review') && <th>Review</th>}
                <th></th>
              </tr>
            </thead>

            <tbody>
              {displayOrders.map((order, idx) => {
                const isSelected = selectedIds.includes(order.id);
                const agentHeader = agentGroups[idx];
                const colCount = visibleCols.length + 3;
                return (
                  <React.Fragment key={order.id}>
                    {agentHeader && (
                      <tr>
                        <td colSpan={colCount} style={{
                          background: 'rgba(99,102,241,0.1)',
                          borderTop: idx > 0 ? '2px solid var(--accent)' : 'none',
                          borderBottom: '1px solid rgba(99,102,241,0.3)',
                          padding: '8px 14px',
                          fontFamily: 'Syne', fontWeight: 700, fontSize: 12,
                          color: 'var(--accent)', letterSpacing: '0.05em',
                        }}>
                          👤 {agentHeader.name}
                          <span style={{ marginLeft: 8, fontWeight: 400, color: 'var(--text3)', fontSize: 11 }}>
                            {agentHeader.count} Bestellung(en)
                          </span>
                        </td>
                      </tr>
                    )}
                    <tr
                      style={{ background: isSelected ? 'rgba(99,102,241,0.07)' : undefined }}
                    >
                    <td style={{ paddingLeft: 14 }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRow(order.id)}
                        style={{ width: 'auto', cursor: 'pointer' }}
                      />
                    </td>

                    {col('date') && <td style={{ whiteSpace: 'nowrap', color: 'var(--text3)', fontSize: 12 }}>{formatDate(order.order_date)}</td>}

                    {col('product') && (
                      <td style={{ maxWidth: 180 }}>
                        {order.asin ? (
                          <a href={`https://www.amazon.de/dp/${order.asin}`} target="_blank" rel="noreferrer"
                            style={{ color: 'var(--text)', textDecoration: 'none', fontSize: 13 }} title={order.product_name}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {order.product_name || order.asin}
                            </div>
                          </a>
                        ) : (
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}>
                            {order.product_name || '-'}
                          </div>
                        )}
                        {order.asin_description && (
                          <div title={order.asin_description} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 2, background: '#f9731618', border: '1px solid #f9731640', borderRadius: 4, padding: '1px 5px', cursor: 'help' }}>
                            <span style={{ fontSize: 9, color: '#f97316', fontWeight: 700, fontFamily: 'Syne' }}>⚠ INFOS</span>
                          </div>
                        )}
                      </td>
                    )}

                    {col('order_nr') && <td style={{ fontFamily: 'DM Mono', fontSize: 11, color: 'var(--text3)' }}>{order.order_number}</td>}

                    {col('agent') && (
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <MessengerBadge platform={order.platform || 'WeChat'} />
                          <span style={{ color: 'var(--accent)', fontSize: 12, fontWeight: 600 }}>{order.agent_name || '-'}</span>
                        </div>
                      </td>
                    )}

                    {col('account') && <td style={{ fontSize: 12, color: 'var(--text2)' }}>{order.account_name || '-'}</td>}
                    {col('price') && <td style={{ fontSize: 12, color: 'var(--text2)' }}>{formatEur(order.price)}</td>}
                    {col('tax_ref') && <td><span className={`badge ${order.tax_ref ? 'badge-green' : 'badge-gray'}`} style={{ fontSize: 10 }}>{order.tax_ref ? 'Ja' : 'Nein'}</span></td>}
                    {col('commission') && <td style={{ fontSize: 12, color: 'var(--text2)' }}>{formatEur(order.commission)}</td>}
                    {col('req_type') && <td style={{ fontSize: 12, color: 'var(--text2)' }}>{order.req_type || '-'}</td>}
                    {col('status') && <td><span className={`badge ${STATUS_COLORS[order.status] || 'badge-gray'}`} style={{ fontSize: 11 }}>{order.status}</span></td>}
                    {col('betrag') && <td style={{ color: 'var(--accent2)', fontWeight: 600, fontSize: 12 }}>{formatEur(order.betrag)}</td>}
                    {col('review') && <td><span className={`badge ${order.review_generated ? 'badge-green' : 'badge-gray'}`} style={{ fontSize: 10 }}>{order.review_generated ? '✓' : '⚪'}</span></td>}

                    <td>
                      <button onClick={() => openEdit(order)}
                        className="btn btn-ghost" style={{ padding: '4px 8px' }}
                        title={order.asin ? `Alle Bestellungen: ${order.asin}` : 'Bestellung bearbeiten'}>
                        <Edit2 size={12} />
                      </button>
                    </td>
                  </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Fußzeile: Summe + Pagination */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid var(--border)', background: 'var(--bg3)' }}>
          {/* Summe der angezeigten Bestellungen */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span style={{ color: 'var(--text3)', fontSize: 12 }}>
              {total} Einträge gesamt
            </span>
            {orders.length > 0 && (
              <>
                <span style={{ color: 'var(--border)' }}>|</span>
                <span style={{ fontSize: 12, color: 'var(--text2)' }}>
                  Diese Seite: <strong style={{ color: 'var(--accent2)', fontFamily: 'DM Mono' }}>
                    {orders.reduce((s, o) => s + parseFloat(o.betrag || 0), 0).toFixed(2).replace('.', ',')} €
                  </strong>
                  <span style={{ color: 'var(--text3)', marginLeft: 6 }}>({orders.length} Bestellungen)</span>
                </span>
              </>
            )}
          </div>
          {/* Pagination */}
          {pages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: 'var(--text3)', fontSize: 12 }}>Seite {page} von {pages}</span>
              <button className="btn btn-ghost" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '6px 10px' }}><ChevronLeft size={14} /></button>
              <button className="btn btn-ghost" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} style={{ padding: '6px 10px' }}><ChevronRight size={14} /></button>
            </div>
          )}
        </div>
      </div>

{showNewOrder && (
        <NewOrderModal onClose={() => setShowNewOrder(false)} onDone={() => { fetchOrders(); fetchStats(); }} />
      )}

      {editModal && (
        <EditModal
          asin={editModal.asin}
          productName={editModal.productName}
          noAsin={editModal.noAsin}
          order={editModal.order}
          onClose={() => setEditModal(null)}
          onDone={() => { fetchOrders(); fetchStats(); }}
        />
      )}
    </div>
  );
}